// API 金鑰的範圍勾選（建立與〔修改範圍〕共用，plan SPEC #api-keys-scopes / #api-keys-edit-scopes）：
// 影像、devices.console、devices.control、devices.maintain、pairing.allocate 勾了要再勾一次確認。
import { useTranslation } from "react-i18next";
import {
  API_KEY_CONSOLE_SCOPE,
  API_KEY_CONTROL_SCOPE,
  API_KEY_MAINTAIN_SCOPE,
  API_KEY_MEDIA_SCOPES,
  API_KEY_PAIRING_SCOPE,
  API_KEY_SCOPE_GROUPS,
} from "../../lib/session";

export type ScopeConfirms = { media: boolean; console: boolean; control: boolean; maintain: boolean; pairing: boolean };

const scopeKey = (s: string) => s.replace(/\./g, "_");

/** 勾了要確認的範圍都確認過了。 */
export function scopesConfirmed(scopes: string[], ok: ScopeConfirms): boolean {
  return (
    (!scopes.some((s) => API_KEY_MEDIA_SCOPES.includes(s)) || ok.media) &&
    (!scopes.includes(API_KEY_CONSOLE_SCOPE) || ok.console) &&
    (!scopes.includes(API_KEY_CONTROL_SCOPE) || ok.control) &&
    (!scopes.includes(API_KEY_MAINTAIN_SCOPE) || ok.maintain) &&
    (!scopes.includes(API_KEY_PAIRING_SCOPE) || ok.pairing)
  );
}

/** 已經有的敏感範圍視為確認過（〔修改範圍〕預先勾好，同 portal）。 */
export function confirmsFor(scopes: string[]): ScopeConfirms {
  return {
    media: scopes.some((s) => API_KEY_MEDIA_SCOPES.includes(s)),
    console: scopes.includes(API_KEY_CONSOLE_SCOPE),
    control: scopes.includes(API_KEY_CONTROL_SCOPE),
    maintain: scopes.includes(API_KEY_MAINTAIN_SCOPE),
    pairing: scopes.includes(API_KEY_PAIRING_SCOPE),
  };
}

export function ApiKeyScopeFields({
  scopes,
  onChange,
  ok,
  onOk,
}: {
  scopes: string[];
  onChange: (next: string[]) => void;
  ok: ScopeConfirms;
  onOk: (next: ScopeConfirms) => void;
}) {
  const { t } = useTranslation();
  const toggle = (s: string) =>
    onChange(scopes.includes(s) ? scopes.filter((x) => x !== s) : [...scopes, s]);
  const confirm = (kind: keyof ScopeConfirms, label: string) => (
    <label className="check scope-confirm">
      <input
        type="checkbox"
        checked={ok[kind]}
        onChange={(e) => onOk({ ...ok, [kind]: e.target.checked })}
      />{" "}
      {label}
    </label>
  );
  const row = (s: string) => (
    <div key={s} className="scope-item">
      <label className="check">
        <input
          type="checkbox"
          checked={scopes.includes(s)}
          onChange={() => toggle(s)}
        />{" "}
        <span>
          {t(`apikeys.scope_${scopeKey(s)}`)}{" "}
          <span className="muted small">
            {t(`apikeys.scope_${scopeKey(s)}_hint`)}
          </span>
        </span>
      </label>
      {/* 確認勾選緊跟在它的範圍下面（影像那組在組尾，見下） */}
      {s === API_KEY_CONTROL_SCOPE && scopes.includes(s) && confirm("control", t("apikeys.control_confirm"))}
      {s === API_KEY_MAINTAIN_SCOPE && scopes.includes(s) && confirm("maintain", t("apikeys.maintain_confirm"))}
      {s === API_KEY_CONSOLE_SCOPE && scopes.includes(s) && confirm("console", t("apikeys.console_confirm"))}
      {s === API_KEY_PAIRING_SCOPE && scopes.includes(s) && confirm("pairing", t("apikeys.pairing_confirm"))}
    </div>
  );
  return (
    <fieldset className="scope-list">
      <legend>{t("apikeys.scopes")}</legend>
      {API_KEY_SCOPE_GROUPS.map((g) => (
        <div key={g.key} className="scope-group">
          <div className="scope-group-title small muted">{t(`apikeys.group_${g.key}`)}</div>
          {g.scopes.map(row)}
          {g.key === "media" && scopes.some((s) => API_KEY_MEDIA_SCOPES.includes(s)) &&
            confirm("media", t("apikeys.media_confirm"))}
        </div>
      ))}
    </fieldset>
  );
}
